import * as THREE from 'three';
import type { Atlas } from '../textures.ts';
import { MeshBuilder } from './build.ts';

/**
 * The station box: two tracks side by side down the centre, with a platform
 * along each outer wall, so trains run in both directions and each has its own
 * platform to open onto.
 *
 * One merged mesh for everything inside, plus four separate wall meshes so the
 * ones nearest the camera can be hidden as the diorama spins (see walls.ts).
 * They all share the atlas material, so four extra meshes cost four draw calls,
 * not four materials.
 */

/** Half length along X. The room is deliberately long - it is seen landscape. */
export const HALF_X = 26;
/** Half width across Z: both tracks, both platforms, and the walls. */
export const HALF_Z = 11.5;
export const WALL_H = 7;
const WALL_T = 0.6;

export const PLATFORM_TOP = 1.4;
/** Everything within this of the centre line is track; beyond it, platform. */
const TRACK_ZONE = 4.3;
const EDGE_STRIP = 0.9;

/** Centre line of each track. The two run side by side down the middle. */
export const TRACK_Z = [-2.3, 2.3] as const;

const OPENING_HALF = 1.75; // tunnel mouth half-width
const OPENING_H = 4.4;

export interface Wall {
  mesh: THREE.Mesh;
  /** Points into the room. */
  normal: THREE.Vector3;
  center: THREE.Vector3;
}

export interface Station {
  group: THREE.Group;
  walls: Wall[];
}

export function createStation(atlas: Atlas, material: THREE.Material): Station {
  const group = new THREE.Group();
  const b = new MeshBuilder();

  const dark = atlas.tile('dark');

  // Single recessed trough carrying both tracks.
  b.box(
    [0, -0.25, 0],
    [HALF_X * 2, 0.5, TRACK_ZONE * 2],
    { all: dark },
    0xffffff,
    3,
  );

  // Rubber-tyred stock runs on flat roll ways rather than rails - a small
  // detail, but it is the giveaway that this is Montreal and nowhere else.
  for (const trackZ of TRACK_Z) {
    for (const offset of [-1.0, 1.0]) {
      b.box([0, 0.12, trackZ + offset], [HALF_X * 2, 0.24, 0.8], { all: dark }, 0xb0a99a, 3);
    }
  }

  // A platform along each outer wall, with a tactile strip on its track edge.
  for (const side of [-1, 1]) {
    const inner = side * TRACK_ZONE;
    const outer = side * HALF_Z;
    const bodyInner = side * (TRACK_ZONE + EDGE_STRIP);

    b.box(
      [0, PLATFORM_TOP / 2, (bodyInner + outer) / 2],
      [HALF_X * 2, PLATFORM_TOP, Math.abs(outer - bodyInner)],
      { py: atlas.tile('platform'), sides: atlas.tile('platform') },
      0xffffff,
      2.5,
    );
    b.box(
      [0, PLATFORM_TOP / 2, (inner + bodyInner) / 2],
      [HALF_X * 2, PLATFORM_TOP, EDGE_STRIP],
      { py: atlas.tile('platformEdge'), sides: atlas.tile('platform') },
      0xffffff,
      2,
    );

    // No pillars. Any spacing that read as structure also stood between the
    // camera and the trains, which are the thing worth looking at.
    const benchZ = side * (HALF_Z - 1.2);
    for (const x of [-15.5, -7.5, 0.5, 8.5, 16.5]) {
      b.box([x, PLATFORM_TOP + 0.55, benchZ], [3.2, 0.25, 1.0], { all: atlas.tile('bench') });
      for (const dx of [-1.3, 1.3]) {
        b.box([x + dx, PLATFORM_TOP + 0.25, benchZ], [0.25, 0.6, 0.9], { all: dark });
      }
    }
  }

  const interior = new THREE.Mesh(b.build(), material);
  interior.frustumCulled = false;
  group.add(interior);

  const walls: Wall[] = [
    buildSideWall(atlas, material, -1),
    buildSideWall(atlas, material, 1),
    buildEndWall(atlas, material, -1),
    buildEndWall(atlas, material, 1),
  ];
  for (const wall of walls) group.add(wall.mesh);

  return { group, walls };
}

/** A long wall running along X, behind one of the platforms. */
function buildSideWall(atlas: Atlas, material: THREE.Material, sign: number): Wall {
  const b = new MeshBuilder();
  const z = sign * (HALF_Z + WALL_T / 2);

  b.box([0, WALL_H / 2, z], [HALF_X * 2, WALL_H, WALL_T], { all: atlas.tile('wall') }, 0xffffff, 2);
  // Accent band at eye height, the way tiled stations break up a long wall.
  b.box(
    [0, 2.8, z - sign * WALL_T * 0.55],
    [HALF_X * 2, 1.6, WALL_T * 0.2],
    { all: atlas.tile('wallAccent') },
    0xffffff,
    2,
  );
  for (const x of [-13, 0, 13]) {
    b.box([x, 4.8, z - sign * WALL_T * 0.6], [4.4, 1.5, WALL_T * 0.3], {
      all: atlas.tile('sign'),
    });
  }

  return wall(
    b.build(),
    material,
    new THREE.Vector3(0, 0, -sign),
    new THREE.Vector3(0, WALL_H / 2, z),
  );
}

/**
 * An end wall, built as piers and lintels around a tunnel mouth for each track.
 * The mouths need no tunnel geometry behind them: the train is clipped at the
 * wall line and fog covers the rest.
 */
function buildEndWall(atlas: Atlas, material: THREE.Material, sign: number): Wall {
  const b = new MeshBuilder();
  const x = sign * (HALF_X + WALL_T / 2);
  const tile = atlas.tile('wall');

  const gaps = TRACK_Z.map((z) => [z - OPENING_HALF, z + OPENING_HALF] as const);
  // Solid stretches: outside each mouth, and the pier between the two.
  const solids: [number, number][] = [
    [-HALF_Z, gaps[0][0]],
    [gaps[0][1], gaps[1][0]],
    [gaps[1][1], HALF_Z],
  ];

  for (const [z0, z1] of solids) {
    if (z1 - z0 <= 0.001) continue;
    b.box([x, WALL_H / 2, (z0 + z1) / 2], [WALL_T, WALL_H, z1 - z0], { all: tile }, 0xffffff, 2);
  }

  for (const [z0, z1] of gaps) {
    // Lintel over the mouth.
    b.box(
      [x, (OPENING_H + WALL_H) / 2, (z0 + z1) / 2],
      [WALL_T, WALL_H - OPENING_H, z1 - z0],
      { all: tile },
      0xffffff,
      2,
    );
    // A dark surround so the mouth reads as a hole rather than a gap in a fence.
    b.box(
      [x - sign * WALL_T, OPENING_H / 2, (z0 + z1) / 2],
      [WALL_T * 0.4, OPENING_H, z1 - z0 + 0.4],
      { all: atlas.tile('dark') },
      0x9a9a9a,
    );
  }

  return wall(
    b.build(),
    material,
    new THREE.Vector3(-sign, 0, 0),
    new THREE.Vector3(x, WALL_H / 2, 0),
  );
}

function wall(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  normal: THREE.Vector3,
  center: THREE.Vector3,
): Wall {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  return { mesh, normal, center };
}
